// react
import { useCallback } from 'react';

// hooks
import { useApp } from 'hooks/useApp';

// store
import { useSelector, dispatch } from 'store';
import { newToast } from 'store/slices/uiSlice';
import {
  useRegisterMemberByDomainMutation,
  useRegisterMemberByInviteKeyMutation,
} from 'store/api/iam';
import { getEmail } from 'store/slices/registrationSlice';
import { useNavigate } from 'react-router-dom';
import { useGoBack } from 'hooks/useGoBack';
import { ROUTES } from 'router';

// components
import { InviteCodeV2 as EnterInviteCodePage } from '@karehero/llama/pwa/pages/InviteCodeV2/InviteCodeV2';

/**
 * EnterInviteCode is a component that renders the enter invite code page.
 */
export const EnterInviteCode = () => {
  // hooks
  const navigate = useNavigate();
  const goBack = useGoBack();
  const [registerByInviteKey] = useRegisterMemberByInviteKeyMutation();
  const [registerMemberByDomain] = useRegisterMemberByDomainMutation();
  const email = useSelector(getEmail);

  // methods
  const onSubmit = useCallback(
    async (code: string) => {
      console.log('[EnterInviteCode] Submit', code);

      if (code.length !== 6) {
        dispatch(
          newToast({
            title: 'Error',
            description: 'Please enter a valid invite code (6 digits)',
            variant: 'error',
          }),
        );
        return;
      }

      const res = await registerByInviteKey({ inviteKey: code });
      if ('data' in res) {
        console.log(
          '[EnterInviteCode] Register by invite key result',
          res.data,
        );

        navigate(ROUTES.VERIFY_SUCCESS);
      } else {
        dispatch(
          newToast({
            title: 'Error',
            description:
              'The code you entered is incorrect. Please ensure you are using the invite code provided in your work email inbox with the subject: "Your KareHero work email verification code is ●●●●●●"',
            variant: 'error',
          }),
        );
      }
    },
    [navigate, registerByInviteKey],
  );

  const onResend = useCallback(async () => {
    const res = await registerMemberByDomain({
      email: email,
    });
    if ('error' in res && res.error && 'status' in res.error) {
      console.error(
        '[EnterInviteCode] Request resend invite code error',
        res.error,
      );
      return;
    }

    if ('data' in res && res.data?.isInviteCode) {
      console.log(
        '[EnterInviteCode] Request resend invite code result',
        res.data,
      );
      return;
    }

    dispatch(
      newToast({
        title: 'Success',
        description: 'Email verification code requested, check your inbox!',
        variant: 'success',
      }),
    );
  }, [dispatch, registerMemberByDomain, email]);

  return (
    <EnterInviteCodePage
      email={email}
      onSubmit={onSubmit}
      onResend={onResend}
      onBack={goBack}
    />
  );
};

EnterInviteCode.displayName = 'EnterInviteCode';
export default EnterInviteCode;
